'use client';

import React from 'react';

interface ProjectProgressHeroProps {
  project?: any;
}

export default function ProjectProgressHero({ project }: ProjectProgressHeroProps) {
  const progress = project?.progress ?? 0;
  const stage = project?.stage ?? 'discovery';

  const stages = ['discovery', 'design', 'procurement', 'construction', 'finishing', 'handover'];
  const stageLabels: Record<string, string> = {
    discovery: 'Discovery',
    design: 'Design',
    procurement: 'Procurement',
    construction: 'Construction',
    finishing: 'Finishing',
    handover: 'Handover',
  };
  const currentStageIndex = stages.indexOf(stage);

  const circumference = 2 * Math.PI * 54;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="card p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
        {/* Radial progress */}
        <div className="relative flex-shrink-0">
          <svg width="128" height="128" viewBox="0 0 128 128" className="-rotate-90">
            <circle cx="64" cy="64" r="54" fill="none" stroke="var(--muted)" strokeWidth="10" />
            <circle
              cx="64" cy="64" r="54"
              fill="none"
              stroke="var(--primary)"
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-700"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-bold text-foreground">{progress}%</span>
            <span className="text-xs text-muted-foreground">Complete</span>
          </div>
        </div>

        {/* Stage info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-semibold text-foreground">Current Phase:</span>
            <span className="px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold">
              {stageLabels[stage]}
            </span>
          </div>

          {/* Stage progress bar */}
          <div className="flex items-center gap-1 flex-wrap">
            {stages.map((s, i) => (
              <React.Fragment key={s}>
                <div className={`flex flex-col items-center gap-1`}>
                  <div className={`w-3 h-3 rounded-full border-2 transition-all ${
                    i < currentStageIndex
                      ? 'bg-positive border-positive'
                      : i === currentStageIndex
                      ? 'bg-primary border-primary' :'bg-card border-border'
                  }`} />
                  <span className={`text-xs hidden sm:block ${i === currentStageIndex ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                    {stageLabels[s]}
                  </span>
                </div>
                {i < stages.length - 1 && (
                  <div className={`flex-1 h-0.5 mb-4 min-w-4 ${i < currentStageIndex ? 'bg-positive' : 'bg-border'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}